import {
  CreateFlowSessionRequest,
  FlowSession,
  InboxItem,
  MessagesResponse,
  UpdateFlowSessionRequest,
  UpdateInboxItemRequest,
} from "@cohesive/api-client";
import {
  InfiniteData,
  QueryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { cloneDeep } from "lodash";
import { orgAPI } from "./api";
import { invalidateArchivedQueries } from "./archived";

// React Query Hooks
export function useFlowSessionById(
  flowSessionId: string,
  options?: Partial<UseQueryOptions<FlowSession, AxiosError>>,
) {
  return useQuery<FlowSession, AxiosError>({
    enabled: !!flowSessionId,
    queryKey: [`flowSession`, flowSessionId],
    queryFn: async () => {
      const response = await orgAPI.flowSessions.getById(flowSessionId);
      return response.data.flow_session;
    },
    ...options,
  });
}

export function useCreateFlowSession(
  options?: UseMutationOptions<
    FlowSession,
    AxiosError,
    CreateFlowSessionRequest
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<FlowSession, AxiosError, CreateFlowSessionRequest>({
    mutationFn: async (request) => {
      const response = await orgAPI.flowSessions.create(request);
      return response.data.flow_session;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(["flowSession", data.id], data);
      const canvasId = variables.canvas_id;
      if (canvasId) {
        queryClient.setQueryData<FlowSession[]>(
          ["canvas-flow-sessions", canvasId],
          (prev) => {
            if (!prev) return [data];
            if (prev.some((session) => session.id === data.id)) {
              return prev.map((session) =>
                session.id === data.id ? { ...session, ...data } : session,
              );
            }
            return [data, ...prev];
          },
        );
      }
      const workspaceId = variables.workspace_id;
      if (workspaceId) {
        queryClient.invalidateQueries({
          queryKey: ["workspace-flow-sessions", workspaceId],
        });
        queryClient.invalidateQueries({
          queryKey: ["workspace-flow-sessions", "recent"],
        });
      }
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useDeleteFlowSession(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: async (flowSessionId) => {
      await orgAPI.flowSessions.delete(flowSessionId);
    },
    ...options,
    onSuccess: (...args) => {
      // Deleting a chat cascades to its files on the backend, so invalidate
      // every cache that can contain trashed resources.
      invalidateArchivedQueries(queryClient);
      options?.onSuccess?.(...args);
    },
  });
}

export function useSetFlowSessionName(
  options?: UseMutationOptions<
    FlowSession,
    AxiosError,
    { flowSessionId: string; name: string }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<
    FlowSession,
    AxiosError,
    { flowSessionId: string; name: string }
  >({
    mutationFn: async ({ flowSessionId, name }) => {
      const response = await orgAPI.flowSessions.updateName(flowSessionId, {
        name,
      });
      return response.data.flow_session;
    },
    ...options,
    onSuccess: (...args) => {
      const [data] = args;
      queryClient.setQueryData(["flowSession", data.id], data);
      patchWorkspaceFlowSessionNameInLists(queryClient, data.id, data.name);
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateFlowSession(
  options?: UseMutationOptions<
    FlowSession,
    AxiosError,
    { flowSessionId: string; data: UpdateFlowSessionRequest }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<
    FlowSession,
    AxiosError,
    { flowSessionId: string; data: UpdateFlowSessionRequest }
  >({
    mutationFn: async ({ flowSessionId, data }) => {
      const response = await orgAPI.flowSessions.update(flowSessionId, data);
      return response.data.flow_session;
    },
    ...options,
    onSuccess: (...args) => {
      const [data] = args;
      queryClient.invalidateQueries({
        queryKey: [`flowSession`, data.id],
      });
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateFlowSessionReadStatus(
  options?: UseMutationOptions<
    FlowSession,
    AxiosError,
    { flowSessionId: string; readStatus: string }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<
    FlowSession,
    AxiosError,
    { flowSessionId: string; readStatus: string }
  >({
    mutationFn: async ({ flowSessionId, readStatus }) => {
      const response = await orgAPI.flowSessions.updateReadStatus(
        flowSessionId,
        { read_status: readStatus },
      );
      return response.data.flow_session;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["flowSession", data.id] });
    },
    ...options,
  });
}

export function useWorkspaceFlowSessions(
  workspaceId: string | undefined,
  options?: Partial<UseQueryOptions<InboxItem[], AxiosError>>,
) {
  return useQuery<InboxItem[], AxiosError>({
    enabled: !!workspaceId,
    queryKey: ["workspace-flow-sessions", workspaceId],
    queryFn: async () => {
      const response = await orgAPI.inbox.list({
        params: { resource_type: "workspace", resource_id: workspaceId },
      });
      return response.data.inbox_items;
    },
    ...options,
  });
}

export function useRecentWorkspaceFlowSessions(
  limit = 10,
  options?: Partial<UseQueryOptions<InboxItem[], AxiosError>>,
) {
  return useQuery<InboxItem[], AxiosError>({
    queryKey: ["workspace-flow-sessions", "recent", limit],
    queryFn: async () => {
      const response = await orgAPI.inbox.list({
        params: { resource_type: "workspace", limit: String(limit) },
      });
      return response.data.inbox_items;
    },
    ...options,
  });
}

/**
 * Workspace chats the user has starred ("pinned"), across all workspaces.
 * The key lives under the `["workspace-flow-sessions"]` prefix so the
 * optimistic star patch in `useUpdateWorkspaceInboxItem` edits cached rows in
 * place; consumers should render-filter on `is_starred` so an optimistic
 * unstar removes the row immediately (membership resyncs on settle).
 */
export function useStarredWorkspaceFlowSessions(
  options?: Partial<UseQueryOptions<InboxItem[], AxiosError>>,
) {
  return useQuery<InboxItem[], AxiosError>({
    queryKey: ["workspace-flow-sessions", "starred"],
    queryFn: async () => {
      const response = await orgAPI.inbox.list({
        params: { resource_type: "workspace", is_starred: true },
      });
      return response.data.inbox_items;
    },
    ...options,
  });
}

/**
 * Starred workspace chats within a single workspace. Filtered server-side
 * (`resource_id` + `is_starred`) rather than by narrowing the org-wide starred
 * list: `InboxItem` only exposes its workspace through the *optional* nested
 * `flow_session`, so a client-side filter would silently drop rows whose
 * relation the API omitted. Same `["workspace-flow-sessions"]` key prefix, so
 * the optimistic star patch still edits these cached rows in place.
 *
 * Key order matters: `"starred"` before the workspace id, so the star
 * mutation's `["workspace-flow-sessions", "starred"]` invalidation covers
 * this list too — an optimistic patch can only edit rows already cached, so
 * a *newly* starred chat joins the scoped list via that refetch.
 */
export function useWorkspaceStarredFlowSessions(
  workspaceId: string | undefined,
  options?: Partial<UseQueryOptions<InboxItem[], AxiosError>>,
) {
  return useQuery<InboxItem[], AxiosError>({
    queryKey: ["workspace-flow-sessions", "starred", workspaceId],
    queryFn: async () => {
      const response = await orgAPI.inbox.list({
        params: {
          resource_type: "workspace",
          resource_id: workspaceId,
          is_starred: true,
        },
      });
      return response.data.inbox_items;
    },
    ...options,
    enabled: !!workspaceId && (options?.enabled ?? true),
  });
}

/** Merge a flow session rename into every workspace chat list cache. */
export function patchWorkspaceFlowSessionNameInLists(
  queryClient: QueryClient,
  flowSessionId: string,
  name: string,
) {
  queryClient.setQueriesData<InboxItem[]>(
    { queryKey: ["workspace-flow-sessions"] },
    (items) => {
      if (!items) {
        return items;
      }
      let changed = false;
      const next = items.map((item) => {
        if (item.flow_session_id !== flowSessionId || !item.flow_session) {
          return item;
        }
        if (item.flow_session.name === name) {
          return item;
        }
        changed = true;
        return {
          ...item,
          flow_session: { ...item.flow_session, name },
        };
      });
      return changed ? next : items;
    },
  );
}

/**
 * Optimistically updates a workspace chat InboxItem (star / read / archive)
 * across the workspace + recents list caches, then persists via the API.
 *
 * Distinct from `@/api/inbox`'s `useUpdateInboxItem`, which maintains the
 * `["inboxItems"]` caches — the workspace chat lists live under
 * `["workspace-flow-sessions", ...]`. Patching by key prefix covers both the
 * per-workspace list and the recents list (any limit) without hardcoding keys.
 */
export function useUpdateWorkspaceInboxItem() {
  const queryClient = useQueryClient();

  return useMutation<
    InboxItem,
    AxiosError,
    { id: string } & UpdateInboxItemRequest
  >({
    mutationFn: async ({ id, ...update }) => {
      const response = await orgAPI.inbox.update(id, update);
      return response.data.inbox_item;
    },
    onMutate: ({ id, ...update }) => {
      queryClient.setQueriesData<InboxItem[]>(
        { queryKey: ["workspace-flow-sessions"] },
        (items) =>
          items?.map((item) =>
            item.id === id ? { ...item, ...update } : item,
          ),
      );
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: ["workspace-flow-sessions"],
      });
    },
    onSettled: (_data, _error, { is_starred }) => {
      // Starring changes pinned-list membership; the optimistic patch only
      // edits rows already cached under the starred keys, so resync them.
      // The prefix covers the org-wide list and every workspace-scoped one
      // (["workspace-flow-sessions", "starred", workspaceId]).
      if (is_starred !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ["workspace-flow-sessions", "starred"],
        });
      }
    },
  });
}

export function useCancelFlowSession(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: async (flowSessionId) => {
      await orgAPI.flowSessions.cancel(flowSessionId);
    },
    ...options,
    onSuccess: (_, flowSessionId) => {
      queryClient.invalidateQueries({
        queryKey: ["flowSession", flowSessionId],
        refetchType: "all",
      });

      queryClient.setQueryData<InfiniteData<MessagesResponse>>(
        ["flowMessages", flowSessionId],
        (prev) => {
          if (prev) {
            const next = cloneDeep(prev);

            next.pages[0].data.flow_messages[0].status = "cancelled";

            return next;
          }

          return prev;
        },
      );
    },
  });
}
